import {
  getApplication,
  getUrlSearchParamValue,
  withLoading,
} from 'src/lib/utils';
import { MapDef } from '../game/defs/map';
import { getEditorMaxLevel, setEditorOverride } from '../match2.assets';
import { match2OpenGame } from '../match2.sequence';
import { getPuzzleStartData } from '../match2.analytics';
import Match2EditorScene from 'src/game/scenes/Match2EditorScene';
import { Match2PowerupType } from 'src/replicant/match2/match2.ruleset';
import { clearMatch2GameplayState } from '../savestate/match2-savestate.utils';
import { PuzzleMapValidator } from '../game/util/PuzzleMapValidator';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { loadMatch2LevelAssets } from 'src/loadMatch2LevelAssets';
import { PuzzleMapImporter } from '../game/util/PuzzleMapImporter';
import { Feature } from 'src/replicant/analytics';
import type { GameScene } from '../game/GameScene';

/**
 * Check if the app is running in editor mode. Only possible in development mode.
 * In editor mode, the app should open straight into the level editor, allowing only
 * edit, playtest, load & save levels. Everything else in the app should be unavailable.
 */
export function isEditorMode() {
  if (!process.env.IS_DEVELOPMENT) return false;

  const editorLevel = editorGetUrlParamLevel();
  if (!isNaN(editorLevel) && editorLevel > 0) return true;

  if (process.env.IS_EDITOR) return true;

  return false;
}

/**
 * Save starting powerups for the editor
 */
export function editorSetStartingPowerups(powerups: Match2PowerupType[]) {
  localStorage.setItem('editor-starting-powerups', JSON.stringify(powerups));
}

/**
 * Retrieve starting powerups for the editor
 */
export function editorGetStartingPowerups() {
  const powerups = localStorage.getItem('editor-starting-powerups');
  try {
    return JSON.parse(powerups ?? '[]') as Match2PowerupType[];
  } catch (e) {
    return [] as Match2PowerupType[];
  }
}

/**
 * Open a playtest game from the level editor.
 */
export async function editorOpenPlaytestGame(
  level: number,
  mapDef: MapDef,
  selectedPowerups: Match2PowerupType[] = [],
): Promise<GameScene> {
  setEditorOverride(level, mapDef);
  await withLoading(() => loadMatch2LevelAssets(), {
    feature: Feature.EDITOR._,
    subFeature: Feature.EDITOR.LEVEL_ASSETS,
  });

  const game = await match2OpenGame({
    selectedPowerups,
    puzzleStartData: getPuzzleStartData(level, {
      ...mapDef,
      levelVersion: 'editor',
      regionVersion: 'editor',
      levelHash: 'editor',
      regionHash: 'editor',
    }),
    playtest: true,
  });

  // Clear any saved state right after, if playing from the editor
  clearMatch2GameplayState();

  return game;
}

/**
 * Play game from the level editor
 */
export async function editorPlayGame(
  level: number,
  mapDef: MapDef,
  selectedPowerups: Match2PowerupType[] = [],
  overrideAfterComplete?: MapDef,
) {
  const game = await editorOpenPlaytestGame(level, mapDef, selectedPowerups);

  await game.waitForComplete();

  // Inform game result in an alert if running from the editor, so we can know what happened
  // just before transitioning back to the editor.
  const result = game.getResult();
  if (result !== 'quit') {
    window.alert(`Game result: ${result}`);
  }

  game.destroy();

  // Replace overriden map with the new map
  if (overrideAfterComplete) {
    setEditorOverride(level, overrideAfterComplete);
  }

  const screen = await getApplication().screenManager.showScreen('minigame', {
    minigame: 'match2-editor',
  });

  if (screen) {
    (screen.currentScene as Match2EditorScene).setLevel(level);
  }
}

/**
 * Get the url parameter level set (?editor=123), if valid, otherwise return 0
 */
export function editorGetUrlParamLevel() {
  const editorParam = getUrlSearchParamValue('editor') ?? '';
  const editorLevel = parseInt(editorParam);
  if (!isNaN(editorLevel) && editorLevel > 0) return editorLevel;
  return 0;
}

/**
 * Get saved editor level from local storage, if valid, otherwise return 0
 */
export function editorGetLocalStorageLevel() {
  const localStorageLevel = localStorage.getItem('editor-level');
  const localStorageLevelInt = parseInt(localStorageLevel ?? '1');
  if (isNaN(localStorageLevelInt)) return 0;
  return localStorageLevelInt;
}

/**
 * Save level to local storage
 */
export function editorSetLocalStorageLevel(level: number) {
  localStorage.setItem('editor-level', String(level));
}

/**
 * Get initial level for the level editor based on either url param or local storage.
 */
export function editorGetInitialLevel() {
  const maxLevel = getEditorMaxLevel();

  const urlLevel = editorGetUrlParamLevel();
  if (urlLevel > 0 && urlLevel <= maxLevel) return urlLevel;

  const storageLevel = editorGetLocalStorageLevel();
  if (storageLevel > 0 && storageLevel <= maxLevel) return storageLevel;

  return 1;
}

/**
 * Convert data to json and save to file
 */
export function editorDownloadJson(name: string, obj: unknown) {
  const json =
    'data:text/json;charset=utf-8,' +
    encodeURIComponent(JSON.stringify(obj, null, 2));
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', json);
  downloadAnchorNode.setAttribute('download', `${name}.json`);
  downloadAnchorNode.click();
}

/**
 * Upload a single json file and return the parsed object
 */
export async function editorUploadJson() {
  return new Promise((resolve) => {
    let input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    const handler = async () => {
      if (!input.files) {
        console.error('No files uploaded');
        resolve(null);
        return;
      }

      const data = await input.files?.[0].text();
      if (!data) {
        console.error('No text in uploaded file');
        resolve(null);
        return;
      }

      try {
        const obj = JSON.parse(data);
        resolve(obj);
      } catch (e) {
        console.error('Cannot load json file', e);
        resolve(null);
      }
    };
    input.onchange = handler;
    input.click();
  });
}

export function validateLevels(mapDefs: Array<MapDef & { level?: number }>) {
  const errors: string[] = [];
  mapDefs.forEach((mapDef, index) => {
    const result = new PuzzleMapValidator(mapDef)
      .run()
      .map((x) => `\n - ${x}`)
      .join();
    if (result.length > 0) {
      errors.push(`Level ${mapDef.level ?? index + 1}: ${result}`);
    }
  });

  if (errors.length > 0) {
    console.error(errors.join('\n'));

    const maxErrors = 20;
    if (errors.length > maxErrors) {
      const moreErrors = errors.length - maxErrors;
      errors.splice(maxErrors, moreErrors, `... and ${moreErrors} more errors`);
    }

    const errorsText = errors.join('\n');
    void openPopupPromise('popupFullScreenError', {
      title: 'Error validating levels',
      message: errorsText,
    });

    return false;
  }

  return true;
}

export function getEmptyMapDef() {
  const data = `m40o[]r[]s[bb,bg,br,by]g[o9r9c[$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$]]`;
  const mapDef = new PuzzleMapImporter(data).import(false);
  if (!mapDef) throw new Error('Failed to create empty level');
  return mapDef;
}
